/**
 * @voltxampere/web — Damage preferences (Sprint 36-5)
 *
 * AYARLAR > Hasar sub-section toggles + IDB persistence wiring for
 * the Sprint 36-2 damage state machine. Two independent toggles:
 *
 *   - `persistAcrossReload`: when true, the damage map snapshot is
 *     written on every state mutation and restored on boot. When
 *     false, the snapshot is cleared and reload starts fresh
 *     ("clean slate" mode).
 *
 *   - `wireOverloadEnabled`: master toggle for the Sprint 36-3 wire
 *     melt animation. Apps/web canvas reads this to decide whether
 *     to gate per-wire timer increments.
 *
 * Pattern mirrors `explosion-prefs.ts` (Sprint 35-5) +
 * `thermal-prefs.ts` (Sprint 34-5).
 *
 * Defaults: both true. Hydrate uses `?? true` so v4/v5/v6 settings
 * records (no damage field) start with both enabled.
 *
 * Engine zero-diff invariant honored — this module imports nothing
 * from `@voltxampere/engine`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect, signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

import { damageMap, restoreDamageSnapshot, snapshotDamageState } from "./damage-state.js";

/** Master toggle: persist damage state across page reloads. */
export const damagePersistAcrossReloadSignal = signal<boolean>(true);

/** Master toggle: enable wire current overload detection + melt animation. */
export const wireOverloadEnabledSignal = signal<boolean>(true);

let initialised = false;
let snapshotEffectDisposer: (() => void) | null = null;

/**
 * Hydrate both toggles from IDB and (when persistAcrossReload is
 * enabled) restore the persisted damage snapshot. Called once
 * during boot. Returns count of fields hydrated (0..3) for
 * dev-mode logging.
 */
export async function initDamagePrefs(): Promise<number> {
  if (initialised) return 0;
  initialised = true;
  let hydrated = 0;
  try {
    const settings = await getSettings();
    const damage = settings?.damage;
    if (damage) {
      if (typeof damage.persistAcrossReload === "boolean") {
        damagePersistAcrossReloadSignal.value = damage.persistAcrossReload;
        hydrated++;
      }
      if (typeof damage.wireOverloadEnabled === "boolean") {
        wireOverloadEnabledSignal.value = damage.wireOverloadEnabled;
        hydrated++;
      }
      if (
        damagePersistAcrossReloadSignal.value &&
        Array.isArray(damage.snapshot) &&
        damage.snapshot.length > 0
      ) {
        restoreDamageSnapshot(damage.snapshot);
        hydrated++;
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA damage-prefs] hydrate failed:", err);
    }
  }
  installSnapshotPersistence();
  return hydrated;
}

/**
 * Install (or replace) the reactive effect that persists damageMap
 * mutations to IDB whenever `persistAcrossReload` is enabled.
 *
 * The effect runs once per damageMap value change. When the toggle
 * is OFF, the effect writes an empty snapshot to clear any stale
 * residue from a previous "true" period.
 */
function installSnapshotPersistence(): void {
  // Idempotent install — clean up the previous one first.
  if (snapshotEffectDisposer !== null) {
    snapshotEffectDisposer();
    snapshotEffectDisposer = null;
  }
  snapshotEffectDisposer = effect(() => {
    // Read both signals so the effect re-runs on either change.
    const persistOn = damagePersistAcrossReloadSignal.value;
    // Subscribe to damageMap; the value is consumed below.
    const _mapSubscribe = damageMap.value;
    void _mapSubscribe;
    if (!initialised) return;
    const snapshot = persistOn ? snapshotDamageState() : [];
    void mergeSettings({
      damage: {
        persistAcrossReload: damagePersistAcrossReloadSignal.value,
        wireOverloadEnabled: wireOverloadEnabledSignal.value,
        snapshot,
      },
    }).catch((err: unknown) => {
      if (import.meta.env.DEV) {
        console.warn("[VXA damage-prefs] persist failed:", err);
      }
    });
  });
}

function persistTogglesOnly(): void {
  void mergeSettings({
    damage: {
      persistAcrossReload: damagePersistAcrossReloadSignal.value,
      wireOverloadEnabled: wireOverloadEnabledSignal.value,
      snapshot: damagePersistAcrossReloadSignal.value ? snapshotDamageState() : [],
    },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA damage-prefs] persist failed:", err);
    }
  });
}

/** Toggle persistAcrossReload (persists immediately). */
export function setDamagePersistAcrossReload(enabled: boolean): void {
  damagePersistAcrossReloadSignal.value = enabled;
  persistTogglesOnly();
}

/** Toggle wireOverloadEnabled (persists immediately). */
export function setWireOverloadEnabled(enabled: boolean): void {
  wireOverloadEnabledSignal.value = enabled;
  persistTogglesOnly();
}
